//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { QuickLink } from "models/propTypes/commonPropTypes";
//#endregion
//#region components
import { ActionLinksGroup } from "components/common";
import { useLocation, useNavigate } from "react-router-dom";
import useVatReportPermissions from "../../vatReports/usePermissions";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { CompanyService } from "services";

const AdvisorLinks: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { vatReportsAvailable } = useVatReportPermissions();

  const reconciliationDate =
    useSelector((state: AppState) => state.auth.selectedCompany.reconciliationDate) ?? DateHelper.today();
  const reconciliationDateString = DateHelper.convertDateToShortISOString(reconciliationDate);

  const onSalariesClick = () => {
    navigate(UrlHelper.formatPath("/Employees/Salaries"), {
      state: { background: location },
    });
  };

  const onSieFilesClick = () => {
    navigate(UrlHelper.formatPath("/Reports/SIE"), {
      state: { background: location },
    });
  };

  const onBalanceSheetClick = () => {
    navigate(UrlHelper.formatPath("/Reports/BalanceSheet"), {
      state: { background: location },
    });
  };

  const onAccountsPayableClick = () => {
    navigate(UrlHelper.formatPath("/Reports/Expenses/IncomingInvoices?snapshotDate=" + reconciliationDateString), {
      state: { background: location },
    });
  };

  const onAnnualAccountsClick = () => {
    let url = "";
    if (CompanyService.hasSavedFinancialStatements()) {
      url = "/Reports/FinancialStatements";
    } else {
      url = "/Reports/SharedDocuments";
    }
    navigate(UrlHelper.formatPath(url), {
      state: { background: location },
    });
  };

  const onIncomeStatementClick = () => {
    navigate(UrlHelper.formatPath("/Reports/IncomeStatement"), {
      state: { background: location },
    });
  };

  const onAccountsReceivableClick = () => {
    navigate(UrlHelper.formatPath("/Reports/Revenue/Invoices?ledgerDate=" + reconciliationDateString), {
      state: { background: location },
    });
  };

  const onSharedDocumentsClick = () => {
    navigate(UrlHelper.formatPath("/Reports/SharedDocuments"), {
      state: { background: location },
    });
  };

  const onHoldingRegisterClick = () => {
    navigate(UrlHelper.formatPath("/Reports/HoldingRegisters"), {
      state: { background: location },
    });
  };

  const onMonthlyReportsClick = () => {
    navigate(UrlHelper.formatPath("/AdvisoryView/MonthlyReports"), {
      state: { background: location },
    });
  };

  const onVatReportsClick = () => {
    navigate(UrlHelper.formatPath("/AdvisoryView/VatReports"), {
      state: { background: location },
    });
  };

  const onEmailAddressesClick = () => {
    navigate(UrlHelper.formatPath("/AdvisoryView/EmailAddresses"), {
      state: { background: location },
    });
  };

  const links: QuickLink[] = [
    {
      icon: IconDefinitionConstants.WMoneyBag,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.Salaries),
      onClickHandler: onSalariesClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.IncomeStatement),
      onClickHandler: onIncomeStatementClick,
    },
    {
      icon: IconDefinitionConstants.WDocument,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.SIEFiles),
      onClickHandler: onSieFilesClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AccountsReceivableLedger),
      onClickHandler: onAccountsReceivableClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.BalanceSheet),
      onClickHandler: onBalanceSheetClick,
    },
    {
      icon: IconDefinitionConstants.WDocument,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.SharedDocuments),
      onClickHandler: onSharedDocumentsClick,
    },
    {
      icon: IconDefinitionConstants.WReports,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.AccountsPayable),
      onClickHandler: onAccountsPayableClick,
    },
    {
      icon: IconDefinitionConstants.WCompany,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.HoldingRegister.HoldingRegister),
      onClickHandler: onHoldingRegisterClick,
    },
    {
      icon: IconDefinitionConstants.WVat,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.AnnualAccounts),
      onClickHandler: onAnnualAccountsClick,
    },
    {
      icon: IconDefinitionConstants.WReport1,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReports),
      onClickHandler: onMonthlyReportsClick,
    },
    {
      icon: IconDefinitionConstants.WEmail,
      btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.EmailAddresses),
      onClickHandler: onEmailAddressesClick,
    },
    ...(vatReportsAvailable
      ? [
          {
            icon: IconDefinitionConstants.WVat,
            btnText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.VatReports),
            onClickHandler: onVatReportsClick,
          },
        ]
      : []),
  ];

  return <ActionLinksGroup links={links} />;
};

export default AdvisorLinks;
